
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import "../styles/mindmap.css";

const GREY = ["#6e6e6e", "#999999", "#c2c2c2", "#e0e0e0"];

export default function D3Sunburst({ data, userHighlights, width = 340, height = 340 }) {
  const ref = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const userPathsSet = useMemo(() => {
    if (!userHighlights || !userHighlights.length) return new Set();
    const allPaths = [];
    userHighlights.forEach(h => {
      const pathArr = [h.domain, h.intent, h.barrier, h.stage].filter(Boolean);
      for (let i = 0; i < pathArr.length; ++i) {
        allPaths.push(pathArr.slice(0, i + 1).join("|"));
      }
    });
    return new Set(allPaths);
  }, [userHighlights]);

  useEffect(() => {
    const svgEl = d3.select(ref.current);
    svgEl.selectAll("*").remove();
    if (!data) return;

    const radius = Math.min(width, height) / 2;
    const root = d3.hierarchy(data).sum(d => d.size || 0).sort((a, b) => b.value - a.value);
    d3.partition().size([2 * Math.PI, radius])(root);

    const arc = d3.arc()
      .startAngle(d => d.x0)
      .endAngle(d => d.x1)
      .innerRadius(d => d.y0)
      .outerRadius(d => d.y1 - 2);

    const opacities = [0.75, 0.85, 0.95, 1.0]; // higher alpha at all depths

    const svg = svgEl
      .attr("width", width)
      .attr("height", height)
      .style("background", "none")          // ensure no white background
      .style("background-color", "transparent") // some browsers look at this
      .style("fill", "none")                // root svg has no fill
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);

    svg.selectAll("path")
      .data(root.descendants().filter(d => d.depth))
      .enter()
      .append("path")
      .attr("d", arc)
      .attr("fill", function (d) {
        const ancestors = d.ancestors().reverse().map(x => x.data.name).slice(1);
        const pathStr = ancestors.join("|");
        if (userPathsSet.has(pathStr)) {
          const op = opacities[Math.min(ancestors.length - 1, opacities.length - 1)];
          return `rgba(255,191,0,${op})`; // gold overlays for user's slices
        }
        return GREY[Math.min(d.depth - 1, GREY.length - 1)];
      })
      .attr("stroke", "#fff")
      .attr("stroke-width", 2)
      .style("opacity", d => d.value > 0 ? 1 : 0.06)
      .on("mousemove", function (e, d) {
        const path = d.ancestors().reverse().map(x => x.data.name).slice(1).join(" > ");
        setTooltip({ x: e.clientX, y: e.clientY, path, count: d.value });
        d3.select(this).attr("stroke", "#ffbf00").attr("stroke-width", 3);
      })
      .on("mouseleave", function () {
        setTooltip(null);
        d3.select(this).attr("stroke", "#fff").attr("stroke-width", 2);
      });

    svg.append("text")
      .attr("text-anchor", "middle")
      .attr("y", 8)
      .attr("font-size", "18px")
      .attr("font-weight", 700)
      .attr("fill", GREY[2])
      .text("Tabs");
  }, [data, width, height, userPathsSet]);

  return (
    <div className="sunburst-outer-container" style={{ background: "transparent" }}>
      <svg ref={ref} className="sunburst-svg" style={{ background: "transparent" }} />
      {tooltip && (
        <div className="sunburst-tooltip" style={{ top: tooltip.y + 6, left: tooltip.x + 7 }}>
          <b>{tooltip.path}</b><br />{tooltip.count} Tabs
        </div>
      )}
    </div>
  );
}
